<template>
  <div class="retention-images">
    <retention-tabs />

    <div class="page-header">
      <div>
        <h2>Retention Images</h2>
        <p class="hint">
          Upload images once; reference them from any number of retention messages.
          Apple reviews each image independently. Approved images can only be deleted
          when no active message references them.
        </p>
      </div>
      <button
        v-if="canCreateRetentionImage && isConfigured"
        class="btn btn-primary"
        @click="createOpen = true"
      >
        <i class="fas fa-plus"></i> Upload Image
      </button>
      <span v-else-if="!canCreateRetentionImage" class="permission-note">No permission to upload retention images</span>
    </div>

    <div v-if="isLoading" class="empty">Loading…</div>
    <div v-else-if="images.length === 0" class="empty">
      No images yet.
      <span v-if="canCreateRetentionImage">Click "Upload Image" to create your first one.</span>
    </div>

    <template v-else>
      <div class="filter-toolbar">
        <div class="search-input">
          <i class="fas fa-search"></i>
          <input
            v-model="imageSearch"
            type="text"
            placeholder="Search by name…"
          />
          <i v-if="imageSearch" class="fas fa-times clear-icon" @click="imageSearch = ''" />
        </div>
        <div class="usage-filter">
          <button
            v-for="opt in usageFilterOptions"
            :key="opt.value"
            type="button"
            class="usage-filter-pill"
            :class="{ 'usage-filter-pill-active': usageFilter === opt.value }"
            @click="usageFilter = opt.value"
          >
            {{ opt.label }}
            <span v-if="usageCounts[opt.value] !== undefined" class="usage-filter-count">
              {{ usageCounts[opt.value] }}
            </span>
          </button>
        </div>
        <button
          v-if="(canSubmitRetentionImage || canDeleteRetentionImage) && visibleImages.length > 0"
          class="link-btn"
          @click="toggleSelectAllVisible"
        >
          {{ allVisibleSelected ? 'Deselect all' : 'Select all visible' }}
        </button>
        <span class="filter-count">{{ visibleImages.length }} / {{ images.length }}</span>
      </div>

      <div v-if="visibleImages.length === 0" class="empty">No images match your search.</div>

      <div v-else class="gallery">
        <div
          v-for="(img, idx) in visibleImages"
          :key="img.id"
          class="tile"
          :class="{ selected: selectedIds.has(img.id) }"
        >
        <div class="thumb" @click="openLightbox(img)">
          <img v-if="img.imageUrl" :src="img.imageUrl" :alt="img.name || img.imageIdentifier" />
          <div v-else class="thumb-empty">No preview</div>
          <span class="state-pill" :class="`state-${(img.imageState || 'draft').toLowerCase()}`">
            {{ img.imageState || 'draft' }}
          </span>
          <span class="usage-pill" :class="`usage-${usageOf(img)}`" :title="usageTitle(usageOf(img))">
            <i :class="usageIcon(usageOf(img))" />
            {{ usageLabel(usageOf(img)) }}
          </span>
        </div>
        <div class="meta">
          <input
            v-if="canSubmitRetentionImage || canDeleteRetentionImage"
            type="checkbox"
            class="tile-check"
            :checked="selectedIds.has(img.id)"
            @change="toggleItem(img.id, idx, $event)"
          />
          <div class="meta-text">
            <div class="meta-name" :title="img.name">{{ img.name || '(unnamed)' }}</div>
            <div v-if="img.description" class="meta-description" :title="img.description">
              {{ img.description }}
            </div>
            <code
              class="meta-id"
              @click="copy(img.imageIdentifier)"
              :title="`Click to copy: ${img.imageIdentifier}`"
            >
              {{ img.imageIdentifier }}
            </code>
          </div>
          <popup-menu>
            <template v-slot:trigger>
              <i class="fas fa-ellipsis-v" />
            </template>
            <template v-slot:content>
              <div v-if="canCreateRetentionImage">
                <a @click="openEdit(img)">
                  <i class="fas fa-pen-square" /> Edit metadata
                </a>
              </div>
              <div v-if="img.imageState === 'draft' && canSubmitRetentionImage">
                <a @click="submit(img.id)">
                  <i class="fas fa-paper-plane" /> Submit
                </a>
              </div>
              <div v-if="canDeleteRetentionImage">
                <a @click="remove(img.id)" class="danger">
                  <i class="fas fa-trash" /> Delete
                </a>
              </div>
            </template>
          </popup-menu>
        </div>
        <div class="usage" v-if="usageFor(img.imageIdentifier).length > 0">
          <span class="usage-count">
            <i class="fas fa-link" />
            Used by {{ usageFor(img.imageIdentifier).length }}
            {{ usageFor(img.imageIdentifier).length === 1 ? 'message' : 'messages' }}
          </span>
          <div class="usage-chips">
            <a
              v-for="m in usageFor(img.imageIdentifier).slice(0, 3)"
              :key="m.id"
              class="usage-chip"
              @click.stop="goToMessage(m.id)"
              :title="m.name || m.header"
            >
              <span class="usage-chip-title">{{ m.name || m.header || '(unnamed)' }}</span>
            </a>
            <span
              v-if="usageFor(img.imageIdentifier).length > 3"
              class="usage-overflow"
            >
              +{{ usageFor(img.imageIdentifier).length - 3 }} more
            </span>
          </div>
        </div>
        <div class="usage usage-empty" v-else>
          <span class="usage-count muted">
            <i class="fas fa-unlink" /> Unused
          </span>
        </div>
        </div>
      </div>
    </template>

    <!-- Sticky bulk-action bar -->
    <div
      v-if="selectedList.length > 0 && !bulkRunning && (canSubmitRetentionImage || canDeleteRetentionImage)"
      class="bulk-bar"
    >
      <span class="bulk-count">
        <strong>{{ selectedList.length }}</strong>
        {{ selectedList.length === 1 ? 'image' : 'images' }} selected
      </span>
      <button
        v-if="canSubmitRetentionImage"
        class="btn btn-primary"
        :disabled="submittableSelected.length === 0"
        @click="bulkSubmit"
      >
        Submit {{ submittableSelected.length }} {{ submittableSelected.length === 1 ? 'draft' : 'drafts' }}
      </button>
      <button class="link-btn" @click="selectNone">Clear selection</button>
      <div class="bulk-bar-right">
        <button
          v-if="canDeleteRetentionImage"
          class="btn btn-remove-default"
          @click="bulkDelete"
        >
          Delete {{ deletableSelected.length }}
        </button>
      </div>
      <p
        v-if="canSubmitRetentionImage && submittableSelected.length < selectedList.length"
        class="bulk-hint"
      >
        {{ selectedList.length - submittableSelected.length }} selected item(s) aren't drafts and will be skipped on submit.
      </p>
    </div>

    <retention-bulk-runner-modals
      unit="image"
      :running="bulkRunning"
      :progress="bulkProgress"
      :show-result="showResultModal"
      :result="bulkResult"
      :running-verb="bulkRunningVerb"
      :complete-title="bulkCompleteTitle"
      :retryable-failures="retryableFailures"
      :terminal-failures="terminalFailures"
      @retry="retryFailed"
      @close="closeResultModal"
    />

    <retention-image-create
      v-if="createOpen"
      @close="createOpen = false"
      @created="onCreated"
    />

    <retention-image-edit
      v-if="editing"
      :image="editing"
      @close="editing = null"
      @updated="editing = null"
    />

    <lightbox :src="lightboxSrc" :alt="lightboxAlt" @close="lightboxSrc = null" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import RetentionImageCreate from '../../components/Retention/ImageCreate.vue';
import RetentionImageEdit from '../../components/Retention/ImageEdit.vue';
import RetentionBulkRunnerModals from '../../components/Retention/RetentionBulkRunnerModals.vue';
import Lightbox from '../../components/Retention/Lightbox.vue';
import PopupMenu from '../PushNotification/UIComponents/PopupMenu.vue';
import RetentionTabs from './Tabs.vue';
import { permissionsMixin } from '../../mixins/permissions.mixin';
import { retentionBulkRunnerMixin } from '../../mixins/retentionBulkRunner.mixin';
import notify from '../../util/notification';

export default {
  mixins: [permissionsMixin, retentionBulkRunnerMixin],
  components: { RetentionImageCreate, RetentionImageEdit, RetentionBulkRunnerModals, Lightbox, PopupMenu, RetentionTabs },
  data() {
    return {
      createOpen: false,
      editing: null,
      lightboxSrc: null,
      lightboxAlt: '',
      imageSearch: '',
      usageFilter: 'all',
      usageFilterOptions: [
        { value: 'all', label: 'All' },
        { value: 'main', label: 'Main' },
        { value: 'bullet', label: 'Bullet' }
      ],
      selectedIds: new Set(),
      lastClickedIndex: null,
      bulkAction: null
    };
  },
  computed: {
    ...mapGetters('retention', ['isConfiguredFor']),
    isConfigured() {
      return this.isConfiguredFor(this.$store.getters.activeApp);
    },
    images() {
      return this.$store.getters['retention/images'] || [];
    },
    visibleImages() {
      const search = this.imageSearch.trim().toLowerCase();
      const usageFilter = this.usageFilter;
      return this.images.filter((img) => {
        if (search && !(img.name || '').toLowerCase().includes(search)) return false;
        if (usageFilter !== 'all' && this.usageOf(img) !== usageFilter) return false;
        return true;
      });
    },
    // Selection persists across filter changes, so resolve against the full list.
    selectedList() {
      return this.images.filter((img) => this.selectedIds.has(img.id));
    },
    submittableSelected() {
      return this.selectedList.filter((img) => (img.imageState || 'draft') === 'draft');
    },
    deletableSelected() {
      return this.selectedList;
    },
    allVisibleSelected() {
      return (
        this.visibleImages.length > 0 &&
        this.visibleImages.every((img) => this.selectedIds.has(img.id))
      );
    },
    bulkRunningVerb() {
      return this.bulkAction === 'submit' ? 'Submitting' : 'Deleting';
    },
    bulkCompleteTitle() {
      return this.bulkAction === 'submit' ? 'Bulk submit' : 'Bulk delete';
    },
    usageCounts() {
      const counts = { all: this.images.length, main: 0, bullet: 0 };
      for (const img of this.images) {
        const u = this.usageOf(img);
        if (counts[u] !== undefined) counts[u] += 1;
      }
      return counts;
    },
    retentionMessages() {
      return this.$store.getters.retentionMessages || [];
    },
    isLoading() {
      return this.$store.getters['retention/isLoadingImages'];
    },
    imageUsage() {
      // Track each image's usage from BOTH the main-image slot AND every bullet
      // icon — otherwise an image bound to a bulleted_image's bullet slot looks
      // unused in the gallery.
      const map = new Map();
      const push = (id, msg) => {
        if (!id) return;
        if (!map.has(id)) map.set(id, []);
        const list = map.get(id);
        if (!list.some((m) => m.id === msg.id)) list.push(msg);
      };
      for (const m of this.retentionMessages) {
        if (m.imageIdentifier) push(m.imageIdentifier, m);
        if (Array.isArray(m.bulletPoints)) {
          for (const b of m.bulletPoints) push(b?.imageIdentifier, m);
        }
      }
      return map;
    }
  },
  watch: {
    // A filter/data change reindexes the visible list, so a stored shift-click
    // anchor would point at a different tile — drop it.
    visibleImages() {
      this.lastClickedIndex = null;
    }
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('retention/fetchImages'),
      this.$store.dispatch('retention/fetchMessages')
    ]);
  },
  beforeRouteLeave(to, from, next) {
    if (this.bulkRunning) {
      if (!confirm('A bulk operation is still running. Leave anyway?')) return next(false);
    }
    next();
  },
  methods: {
    usageFor(imageIdentifier) {
      return this.imageUsage.get(imageIdentifier) || [];
    },
    // Legacy images uploaded before `usage` existed are physically FULL_SIZE
    // (3840 wide), so surface them as 'main' rather than introducing a third
    // bucket that would let them slip into the bullet picker by accident.
    usageOf(img) {
      return img.usage === 'bullet' ? 'bullet' : 'main';
    },
    usageLabel(usage) {
      return usage === 'bullet' ? 'Bullet' : 'Main';
    },
    usageIcon(usage) {
      return usage === 'bullet' ? 'fas fa-list-ul' : 'fas fa-image';
    },
    usageTitle(usage) {
      return usage === 'bullet'
        ? 'Bullet icon (1024 × 1024 BULLET_POINT)'
        : 'Main image (3840 wide FULL_SIZE)';
    },
    openLightbox(img) {
      if (!img.imageUrl) return;
      this.lightboxSrc = img.imageUrl;
      this.lightboxAlt = img.name || img.imageIdentifier;
    },
    openEdit(img) {
      this.editing = img;
    },
    async submit(id) {
      if (!confirm('Submit this image to Apple for review? It will become immutable.')) return;
      await this.$store.dispatch('retention/submitImage', id);
    },
    async remove(id) {
      if (!confirm('Delete this image? This will also delete it from Apple if it was submitted.')) return;
      await this.$store.dispatch('retention/deleteImage', id);
    },
    onCreated() {
      this.createOpen = false;
    },
    goToMessage(id) {
      this.$router.push({
        name: 'RetentionMessage',
        params: { aid: this.$store.getters.activeApp, id }
      });
    },
    copy(text) {
      const tmp = document.createElement('input');
      tmp.value = text;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand('copy');
      document.body.removeChild(tmp);
    },
    toggleItem(id, idx, event) {
      if (event && event.shiftKey && this.lastClickedIndex !== null) {
        const [from, to] = [this.lastClickedIndex, idx].sort((a, b) => a - b);
        const target = !this.selectedIds.has(id);
        const next = new Set(this.selectedIds);
        for (let i = from; i <= to; i++) {
          const c = this.visibleImages[i]?.id;
          if (!c) continue;
          if (target) next.add(c);
          else next.delete(c);
        }
        this.selectedIds = next;
      } else {
        const next = new Set(this.selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        this.selectedIds = next;
      }
      this.lastClickedIndex = idx;
    },
    toggleSelectAllVisible() {
      const next = new Set(this.selectedIds);
      if (this.allVisibleSelected) {
        for (const img of this.visibleImages) next.delete(img.id);
      } else {
        for (const img of this.visibleImages) next.add(img.id);
      }
      this.selectedIds = next;
    },
    selectNone() {
      this.selectedIds = new Set();
      this.lastClickedIndex = null;
    },
    async bulkSubmit() {
      const items = this.submittableSelected.map((img) => img.id);
      if (items.length === 0) return;
      await this.runBulk(items, 'submit');
    },
    async bulkDelete() {
      const items = this.deletableSelected.map((img) => img.id);
      if (items.length === 0) return;
      if (!confirm(`Delete ${items.length} image${items.length === 1 ? '' : 's'}? This removes them from Apple and cannot be undone.`)) {
        return;
      }
      await this.runBulk(items, 'delete');
    },
    async runBulk(items, action) {
      this.bulkAction = action;
      const worker = async (id) => {
        if (action === 'submit') {
          await this.$store.dispatch('retention/submitImage', { id, silent: true });
        } else {
          await this.$store.dispatch('retention/deleteImage', { id, silent: true });
        }
      };
      await this.executeBulk({ items, worker });

      const ok = this.bulkResult.successes.length;
      const failed = this.bulkResult.failures.length;
      const verb = action === 'submit' ? 'submitted' : 'deleted';
      if (failed === 0) {
        notify(`Bulk ${verb}: ${ok} of ${items.length} succeeded.`, 'success');
        this.selectNone();
      } else {
        notify(`Bulk ${verb}: ${ok} succeeded, ${failed} failed.`, 'error');
        const next = new Set(this.selectedIds);
        for (const id of this.bulkResult.successes) next.delete(id);
        this.selectedIds = next;
      }
    },
    async retryFailed() {
      const items = this.retryableFailures.map((f) => f.item);
      if (items.length === 0) return;
      this.showResultModal = false;
      await this.runBulk(items, this.bulkAction);
    },
    closeResultModal() {
      this.closeBulkResultModal();
    }
  }
};
</script>

<style scoped>
.retention-images {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.25rem 6rem;
  color: rgba(255, 255, 255, 0.88);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.page-header h2 {
  margin: 0 0 0.3rem;
  font-size: 1.5rem;
  color: #fff;
  font-weight: 600;
}

.hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  margin: 0;
  max-width: 70ch;
  line-height: 1.4;
}

.permission-note {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
}

.empty {
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.filter-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.search-input {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 360px;
}

.search-input i.fa-search {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  pointer-events: none;
}

.search-input input {
  width: 100%;
  padding: 0.45rem 2rem 0.45rem 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.85rem;
}

.search-input input:focus {
  outline: none;
  border-color: #03B3DA;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 2px rgba(3, 179, 218, 0.15);
}

.search-input .clear-icon {
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  cursor: pointer;
}

.search-input .clear-icon:hover {
  color: #fff;
}

.filter-count {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.tile {
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.18s ease, transform 0.06s ease;
}

.tile:hover {
  border-color: rgba(3, 179, 218, 0.4);
  transform: translateY(-1px);
}

.tile.selected {
  border-color: #03B3DA;
  box-shadow: 0 0 0 1px #03B3DA;
}

.tile-check {
  cursor: pointer;
  accent-color: #03B3DA;
  margin-top: 0.2rem;
  flex-shrink: 0;
}

.thumb {
  position: relative;
  aspect-ratio: 16 / 9;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-in;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-empty {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  cursor: default;
}

.state-pill {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  backdrop-filter: blur(8px);
}

.state-draft     { background: rgba(20, 20, 30, 0.8);   color: #c8d0d9; }
.state-pending   { background: rgba(255, 192, 50, 0.85); color: #1a1a26; }
.state-approved  { background: rgba(46, 204, 113, 0.85); color: #0d2818; }
.state-rejected  { background: rgba(231, 76, 60, 0.85);  color: #fff; }

.usage-pill {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  backdrop-filter: blur(8px);
}

.usage-pill i {
  font-size: 0.65rem;
}

.usage-main    { background: rgba(52, 152, 219, 0.9); color: #fff; }
.usage-bullet  { background: rgba(155, 89, 182, 0.9); color: #fff; }

.usage-filter {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.usage-filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  transition: all 0.12s ease;
  font-weight: 500;
}

.usage-filter-pill:hover {
  border-color: #03B3DA;
  color: #fff;
}

.usage-filter-pill-active {
  background: rgba(3, 179, 218, 0.25);
  border-color: #03B3DA;
  color: #fff;
}

.usage-filter-count {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  font-variant-numeric: tabular-nums;
  background: rgba(0, 0, 0, 0.18);
  padding: 1px 6px;
  border-radius: 8px;
}

.usage-filter-pill-active .usage-filter-count {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(0, 0, 0, 0.25);
}

.meta {
  padding: 0.6rem 0.8rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
}

.meta-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
}

.meta-name {
  font-size: 0.92rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-description {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.meta-id {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  cursor: copy;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  background: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.meta-id:hover { color: #03B3DA; }

.usage {
  padding: 0.5rem 0.7rem 0.7rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.75rem;
}

.usage-empty { padding-top: 0.4rem; padding-bottom: 0.55rem; }

.usage-count {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  margin-bottom: 0.35rem;
}

.usage-count i {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
}

.usage-count.muted {
  color: rgba(255, 255, 255, 0.35);
  font-weight: 400;
  margin-bottom: 0;
}

.usage-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.usage-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 2px 7px;
  background: rgba(3, 179, 218, 0.1);
  border: 1px solid rgba(3, 179, 218, 0.25);
  border-radius: 999px;
  cursor: pointer;
  color: #b8e8f2;
  text-decoration: none;
  font-size: 0.72rem;
  max-width: 100%;
}

.usage-chip:hover {
  background: rgba(3, 179, 218, 0.18);
  border-color: rgba(3, 179, 218, 0.5);
  color: #fff;
}

.usage-chip code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.55);
  background: none;
  cursor: pointer;
}

.usage-chip-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 9rem;
}

.usage-overflow {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
}

.danger { color: #e74c3c; }

/* Popup menu positioning — anchor to the meta cell instead of body */
.meta ::v-deep .popup-container {
  position: relative;
  display: inline-block;
}

.meta ::v-deep .popup-container .trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}

.meta ::v-deep .popup-container .trigger:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.meta ::v-deep .popup-content {
  top: calc(100% + 4px) !important;
  right: 0 !important;
  left: auto !important;
  background-color: #1a2332 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 6px !important;
  min-width: 160px;
  z-index: 50;
}

.meta ::v-deep .popup-content > div {
  width: 100%;
}

.meta ::v-deep .popup-content a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 10px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.88);
  text-decoration: none;
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
}

.meta ::v-deep .popup-content a:hover {
  background: rgba(255, 255, 255, 0.06);
}

.meta ::v-deep .popup-content a.danger {
  color: #e74c3c;
}

.meta ::v-deep .popup-content a.danger:hover {
  background: rgba(231, 76, 60, 0.12);
}

.btn {
  padding: 0.5rem 1.1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
}

.btn-primary {
  background: #03B3DA;
  color: #fff;
}

.btn-primary:hover { background: #04a3c5; }
.btn-primary:disabled {
  background: rgba(3, 179, 218, 0.35);
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}

/* Custom danger button — avoids the global template's .btn-danger gradient. */
.btn-remove-default {
  background: rgba(231, 76, 60, 0.12);
  color: #ff6b5e;
  border: 1px solid rgba(231, 76, 60, 0.5);
}
.btn-remove-default:hover {
  background: #e74c3c;
  color: #fff;
  border-color: #e74c3c;
}

.link-btn {
  background: none;
  border: none;
  color: #03B3DA;
  font-size: 0.82rem;
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
}
.link-btn:hover { background: rgba(3, 179, 218, 0.1); }

/* Sticky bulk action bar */
.bulk-bar {
  position: fixed;
  bottom: 0;
  left: 260px;
  right: 0;
  background: rgba(20, 25, 38, 0.96);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(6px);
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 200;
  flex-wrap: wrap;
}

.bulk-count {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.85);
}
.bulk-count strong { color: #03B3DA; }

.bulk-bar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.bulk-hint {
  width: 100%;
  margin: 0.4rem 0 0;
  font-size: 0.8rem;
  color: #ffb84d;
}
</style>
